import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QRCodeSVG } from 'qrcode.react';
import { ShoppingBag, Search, Eye, QrCode, Copy, CheckCircle, Clock, XCircle, Package as PackageIcon, Loader2 } from 'lucide-react';

const statusCfg = {
  pendente: { label: 'Pendente', cls: 'bg-zinc-400/10 text-zinc-400 border-zinc-400/20' },
  aguardando_pagamento: { label: 'Aguard. Pgto', cls: 'bg-amber-400/10 text-amber-400 border-amber-400/20' },
  pago: { label: 'Pago', cls: 'bg-green-400/10 text-green-400 border-green-400/20' },
  separando: { label: 'Separando', cls: 'bg-purple-400/10 text-purple-400 border-purple-400/20', icon: PackageIcon },
  pronto_retirada: { label: 'Pronto para retirada', cls: 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20', icon: PackageIcon },
  enviado: { label: 'Enviado', cls: 'bg-indigo-400/10 text-indigo-400 border-indigo-400/20', icon: PackageIcon },
  finalizado: { label: 'Finalizado', cls: 'bg-blue-400/10 text-blue-400 border-blue-400/20' },
  cancelado: { label: 'Cancelado', cls: 'bg-red-400/10 text-red-400 border-red-400/20' },
};
const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function PedidosGestorPage() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [detailPedido, setDetailPedido] = useState(null);
  const [pixDialog, setPixDialog] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try { const r = await api.get('/gestor/loja/pedidos'); setPedidos(r.data); }
    catch { toast.error('Erro ao carregar pedidos'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const changeStatus = async (pedidoId, newStatus) => {
    try {
      await api.put(`/gestor/loja/pedidos/${pedidoId}/status`, { status: newStatus });
      toast.success(`Status atualizado para: ${statusCfg[newStatus]?.label || newStatus}`);
      fetchData();
      if (detailPedido?.id === pedidoId) {
        const r = await api.get(`/gestor/loja/pedidos/${pedidoId}`);
        setDetailPedido(r.data);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const gerarPix = async (pedidoId) => {
    setPixLoading(true);
    try {
      const r = await api.post(`/gestor/loja/pedidos/${pedidoId}/gerar-pix`);
      setPixDialog(r.data);
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    setPixLoading(false);
  };

  const copyPix = () => {
    if (pixDialog?.pix_code) { navigator.clipboard.writeText(pixDialog.pix_code); toast.success('Codigo PIX copiado!'); }
  };

  const filtered = pedidos.filter(p => {
    if (filterStatus !== 'todos' && p.status !== filterStatus) return false;
    if (search && !p.aluno_nome?.toLowerCase().includes(search.toLowerCase()) && !p.id.includes(search)) return false;
    return true;
  });

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-48 bg-[#141414]" /><Skeleton className="h-64 bg-[#141414]" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="pedidos-gestor-page">
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">PEDIDOS DA LOJA</h1>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input className="pl-10 bg-[#0A0A0A] border-[#27272A]" placeholder="Buscar por aluno ou ID..." value={search} onChange={e => setSearch(e.target.value)} data-testid="pedidos-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-[#0A0A0A] border-[#27272A]" data-testid="pedidos-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#27272A]">
            <SelectItem value="todos">Todos</SelectItem>
            {Object.entries(statusCfg).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-[#27272A] hover:bg-transparent">
              <TableHead className="text-zinc-400">Pedido</TableHead>
              <TableHead className="text-zinc-400">Aluno</TableHead>
              <TableHead className="text-zinc-400">Itens</TableHead>
              <TableHead className="text-zinc-400">Total</TableHead>
              <TableHead className="text-zinc-400">Status</TableHead>
              <TableHead className="text-zinc-400">Data</TableHead>
              <TableHead className="text-zinc-400 text-right">Acoes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-8"><ShoppingBag className="w-8 h-8 mx-auto mb-2 text-zinc-600" />Nenhum pedido</TableCell></TableRow>
            ) : filtered.map(p => {
              const cfg = statusCfg[p.status] || statusCfg.pendente;
              return (
              <TableRow key={p.id} className="border-[#27272A] hover:bg-[#1C1C1E]" data-testid={`pedido-row-${p.id}`}>
                <TableCell className="text-zinc-400 font-mono text-xs">#{p.id.slice(0, 8)}</TableCell>
                <TableCell className="text-white font-medium">{p.aluno_nome}</TableCell>
                <TableCell className="text-zinc-400">{p.itens?.length || 0} itens</TableCell>
                <TableCell className="text-white font-bold font-['Barlow_Condensed']">{fmt(p.valor_total)}</TableCell>
                <TableCell><Badge className={cfg.cls}>{cfg.label}</Badge></TableCell>
                <TableCell className="text-zinc-500 text-xs">{p.created_at?.split('T')[0]}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => setDetailPedido(p)} className="text-zinc-400 hover:text-white" data-testid={`view-pedido-${p.id}`}><Eye className="w-4 h-4" /></Button>
                  {(p.status === 'pendente' || p.status === 'aguardando_pagamento') && !p.pix_code && (
                    <Button size="sm" variant="ghost" onClick={() => gerarPix(p.id)} disabled={pixLoading} className="text-amber-400 hover:text-amber-300" data-testid={`pix-pedido-${p.id}`}><QrCode className="w-4 h-4" /></Button>
                  )}
                  {p.pix_code && (
                    <Button size="sm" variant="ghost" onClick={() => setPixDialog({ pix_code: p.pix_code, valor: p.valor_total })} className="text-green-400 hover:text-green-300"><QrCode className="w-4 h-4" /></Button>
                  )}
                  {p.status === 'aguardando_pagamento' && (
                    <Button size="sm" variant="ghost" onClick={() => changeStatus(p.id, 'pago')} className="text-green-400 hover:text-green-300" data-testid={`confirm-pedido-${p.id}`}><CheckCircle className="w-4 h-4" /></Button>
                  )}
                  {p.status === 'pago' && (
                    <Button size="sm" variant="ghost" onClick={() => changeStatus(p.id, 'finalizado')} className="text-blue-400 hover:text-blue-300" data-testid={`finalize-pedido-${p.id}`}><PackageIcon className="w-4 h-4" /></Button>
                  )}
                  {!['finalizado', 'cancelado', 'pago'].includes(p.status) && (
                    <Button size="sm" variant="ghost" onClick={() => changeStatus(p.id, 'cancelado')} className="text-zinc-400 hover:text-red-400" data-testid={`cancel-pedido-${p.id}`}><XCircle className="w-4 h-4" /></Button>
                  )}
                </TableCell>
              </TableRow>
            )})}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Detail Dialog */}
      <Dialog open={!!detailPedido} onOpenChange={() => setDetailPedido(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">Pedido #{detailPedido?.id?.slice(0, 8)}</DialogTitle></DialogHeader>
          {detailPedido && (
            <div className="space-y-4" data-testid="pedido-detail-dialog">
              <div className="flex justify-between"><span className="text-zinc-400 text-sm">Aluno</span><span className="text-white text-sm">{detailPedido.aluno_nome}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400 text-sm">Status</span><Badge className={statusCfg[detailPedido.status]?.cls}>{statusCfg[detailPedido.status]?.label}</Badge></div>
              <div className="flex justify-between"><span className="text-zinc-400 text-sm">Data</span><span className="text-white text-sm">{detailPedido.created_at?.split('T')[0]}</span></div>
              {detailPedido.prazo_entrega_texto && (
                <div className="flex justify-between">
                  <span className="text-zinc-400 text-sm">Prazo estimado</span>
                  <span className="text-blue-400 text-sm font-medium">
                    {detailPedido.prazo_entrega_texto}
                  </span>
                </div>
              )}
              {detailPedido.observacoes && <div><span className="text-zinc-400 text-sm">Obs:</span><p className="text-white text-sm mt-1">{detailPedido.observacoes}</p></div>}
              <div className="border-t border-[#27272A] pt-3">
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Itens</p>
                {detailPedido.itens?.map((item, i) => (
                  <div key={i} className="flex justify-between py-1.5 border-b border-[#27272A] last:border-0">
                    <span className="text-sm text-white">{item.quantidade}x {item.nome}</span>
                    <span className="text-sm text-zinc-400">{fmt(item.subtotal)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t border-[#27272A]">
                  <span className="text-sm font-bold text-white">Total</span>
                  <span className="text-lg font-black font-['Barlow_Condensed'] text-white">{fmt(detailPedido.valor_total)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PIX Dialog */}
      <Dialog open={!!pixDialog} onOpenChange={() => setPixDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2"><QrCode className="w-5 h-5 text-[var(--cor-primaria,#007AFF)]" /> PIX do Pedido</DialogTitle></DialogHeader>
          {pixDialog && (
            <div className="space-y-4 text-center" data-testid="pedido-pix-dialog">
              <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">{fmt(pixDialog.valor)}</p>
              <div className="bg-white p-4 rounded-lg inline-block mx-auto"><QRCodeSVG value={pixDialog.pix_code} size={200} /></div>
              <div className="flex gap-2">
                <Input value={pixDialog.pix_code} readOnly className="bg-[#0A0A0A] border-[#27272A] text-xs" />
                <Button size="icon" variant="outline" onClick={copyPix} className="border-[#27272A] shrink-0"><Copy className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-zinc-500">Confirmacao manual do pagamento.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
